import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { RadarIcon, LockIcon, MailIcon, AlertCircleIcon, ArrowRightIcon, LinkedinIcon, ChromeIcon } from 'lucide-react';
import { Button } from './ui/Button';
// Custom Microsoft icon since it's not in Lucide
function MicrosoftIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg viewBox="0 0 23 23" fill="currentColor" {...props} data-id="element-205">
      <path d="M0 0h11v11H0zM12 0h11v11H12zM0 12h11v11H0zM12 12h11v11H12z" data-id="element-206" />
    </svg>;
}
type LoginPageProps = {
  onLogin: () => void;
};
export function LoginPage({
  onLogin
}: LoginPageProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 800));
    if (email === 'daniel.frerichs@asia-pilots.com' && password === 'Letmein@0815') {
      onLogin();
    } else {
      setError('Invalid email or password');
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen w-full bg-slate-950 flex flex-col items-center justify-center p-4 relative overflow-hidden" data-id="element-207">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-208">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] bg-amber-500/5 rounded-full blur-[120px]" data-id="element-209" />
        <div className="absolute top-[40%] -right-[10%] w-[40%] h-[40%] bg-blue-500/5 rounded-full blur-[100px]" data-id="element-210" />
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="w-full max-w-md relative z-10" data-id="element-211">
        {/* Logo Section */}
        <div className="text-center mb-8" data-id="element-212">
          <motion.div initial={{
          scale: 0.8,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          delay: 0.2,
          type: 'spring'
        }} className="inline-flex p-4 bg-amber-500/10 rounded-2xl mb-6 ring-1 ring-amber-500/20 shadow-[0_0_30px_-10px_rgba(245,158,11,0.3)]" data-id="element-213">
            <RadarIcon className="w-12 h-12 text-amber-500" data-id="element-214" />
          </motion.div>
          <h1 className="text-3xl font-bold text-white mb-2 tracking-tight" data-id="element-215">
            China Innovation Cockpit
          </h1>
          <p className="text-slate-400 text-sm" data-id="element-216">
            Your Command Center for China's Innovation Landscape
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-slate-900/50 border border-slate-800 backdrop-blur-xl rounded-2xl p-8 shadow-2xl" data-id="element-217">
          <form onSubmit={handleSubmit} className="space-y-5" data-id="element-218">
            <div className="space-y-1.5" data-id="element-219">
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wider ml-1" data-id="element-220">
                Email Address
              </label>
              <div className="relative group" data-id="element-221">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-222">
                  <MailIcon className="h-5 w-5 text-slate-500 group-focus-within:text-amber-500 transition-colors" data-id="element-223" />
                </div>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="block w-full pl-10 pr-3 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all" placeholder="name@company.com" required data-id="element-224" />
              </div>
            </div>

            <div className="space-y-1.5" data-id="element-225">
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wider ml-1" data-id="element-226">
                Password
              </label>
              <div className="relative group" data-id="element-227">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-228">
                  <LockIcon className="h-5 w-5 text-slate-500 group-focus-within:text-amber-500 transition-colors" data-id="element-229" />
                </div>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="block w-full pl-10 pr-3 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all" placeholder="••••••••" required data-id="element-230" />
              </div>
            </div>

            {error && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} className="flex items-center gap-2 text-red-400 text-sm bg-red-900/20 p-3 rounded-lg border border-red-900/30" data-id="element-231">
                <AlertCircleIcon className="w-4 h-4 shrink-0" data-id="element-232" />
                {error}
              </motion.div>}

            <Button type="submit" className="w-full h-11 text-base font-semibold bg-amber-500 hover:bg-amber-400 text-slate-950 shadow-[0_0_20px_rgba(245,158,11,0.2)] hover:shadow-[0_0_25px_rgba(245,158,11,0.4)] transition-all duration-300" disabled={isLoading} data-id="element-233">
              {isLoading ? <div className="w-5 h-5 border-2 border-slate-900/30 border-t-slate-900 rounded-full animate-spin" data-id="element-234" /> : <span className="flex items-center gap-2" data-id="element-235">
                  Sign In <ArrowRightIcon className="w-4 h-4" data-id="element-236" />
                </span>}
            </Button>
          </form>

          <div className="mt-8" data-id="element-237">
            <div className="relative" data-id="element-238">
              <div className="absolute inset-0 flex items-center" data-id="element-239">
                <div className="w-full border-t border-slate-800" data-id="element-240" />
              </div>
              <div className="relative flex justify-center text-xs uppercase" data-id="element-241">
                <span className="bg-slate-900 px-2 text-slate-500" data-id="element-242">
                  Or continue with
                </span>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-3" data-id="element-243">
              <button className="flex items-center justify-center py-2.5 px-4 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 transition-all group" data-id="element-244">
                <LinkedinIcon className="w-5 h-5 text-slate-400 group-hover:text-[#0077b5] transition-colors" data-id="element-245" />
              </button>
              <button className="flex items-center justify-center py-2.5 px-4 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 transition-all group" data-id="element-246">
                <ChromeIcon className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors" data-id="element-247" />
              </button>
              <button className="flex items-center justify-center py-2.5 px-4 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 transition-all group" data-id="element-248">
                <MicrosoftIcon className="w-5 h-5 text-slate-400 group-hover:text-[#00a4ef] transition-colors" data-id="element-249" />
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center" data-id="element-250">
          <p className="text-slate-500 text-sm font-medium flex items-center justify-center gap-1.5" data-id="element-251">
            Build with <span className="text-red-500 animate-pulse" data-id="element-252">♥️</span> in
            Hamburg by asiapilots
          </p>
        </div>
      </motion.div>
    </div>;
}